<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：ユーザマスタ画面
 *
 * 作成者：2021/11/26 ノア）金
 * 最終修正者：2022/1/11 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.userMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.userMaster.contentTitle') }}</span>
          <span>{{ tableData.length }}</span>
          <span class="subject">{{ $t('master.viewTitle.subject') }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          highlight-current-row
          :stripe="true"
          :row-class-name="tableRowClassName"
          header-row-class-name="tti-master-table-header"
          :empty-text="$t('messages.common.emptyTableData')"
          @selection-change="handleSelectionChange"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- 利用者ID -->
          <el-table-column
            class-name="required"
            prop="userId"
            :label="$t('master.userMaster.userId')"
            width="310"
            align="left"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].userId'" :rules="rules.userId">
                <el-input
                  :id="'tableData[' + scope.$index + '].userId'"
                  :disabled="!canCodeChange(scope.row.dataType)"
                  v-model.trim="scope.row.userId"
                  size="small"
                  @change="onRepeatItemChange(formRef, 'userId')"
                >
                  <template slot="prepend">{{ companyPrefix }}</template>
                </el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 利用者名 -->
          <el-table-column
            class-name="required"
            prop="userName"
            :label="$t('master.userMaster.userName')"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].userName'" :rules="rules.userName">
                <el-input
                  :id="'tableData[' + scope.$index + '].userName'"
                  v-model.trim="scope.row.userName"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- パスワード -->
          <el-table-column
            class-name="required"
            prop="password"
            :label="$t('master.userMaster.password')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].password'" :rules="rules.password">
                <el-input
                  :id="'tableData[' + scope.$index + '].password'"
                  v-model.trim="scope.row.password"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- ユーザ種類 -->
          <el-table-column
            class-name="required"
            prop="role"
            :label="$t('master.userMaster.role')"
            width="150"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].role'" :rules="rules.role">
                <el-select
                  :id="'tableData[' + scope.$index + '].role'"
                  v-model="scope.row.role"
                  size="small"
                  :disabled="canUserTypeChange(scope.row.adminFlg)"
                  @change="onRoleTypeChange(scope.row)"
                >
                  <el-option
                    v-for="item in userRoleList"
                    :key="item.code"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 所属営業所 -->
          <el-table-column
            prop="businessOfficeId"
            :label="$t('master.userMaster.businessOffice')"
            width="218"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].businessOfficeId'">
                <el-select
                  v-model="scope.row.businessOfficeId"
                  size="small"
                  :disabled="canBusinessOfficeChange(scope.row.role)"
                >
                  <el-option
                    v-for="item in businessOfficeMasterData"
                    :key="item.id"
                    :label="getBusinessOfficeLabel(item)"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 連絡先（TEL） -->
          <el-table-column prop="tel" :label="$t('master.userMaster.tel')" width="200" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].tel'" :rules="rules.tel">
                <el-input
                  :id="'tableData[' + scope.$index + '].tel'"
                  v-model.trim="scope.row.tel"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- メールアドレス -->
          <el-table-column prop="mail" :label="$t('master.userMaster.mail')" width="250" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].mail'" :rules="rules.mail">
                <el-input
                  :id="'tableData[' + scope.$index + '].mail'"
                  v-model.trim="scope.row.mail"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- MFAバインドフラグ -->
          <el-table-column
            class-name="required"
            prop="mfaBindFlag"
            :label="$t('master.userMaster.mfaBindFlag')"
            width="150"
            align="center"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].mfaBindFlag'" :rules="rules.mfaBindFlag">
                <el-select
                  :id="'tableData[' + scope.$index + '].mfaBindFlag'"
                  v-model="scope.row.mfaBindFlag"
                  size="small"
                  :disabled="canMFABindStatusChange(scope.row.mfaBindFlag)"
                >
                  <el-option
                    v-for="item in usageStatusList"
                    :key="item.code"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column prop="mfaFlag" :label="$t('master.userMaster.mfaFlag')" width="150" align="center">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].mfaFlag'" :rules="rules.mfaFlag">
                <el-switch
                  v-model="scope.row.mfaFlag"
                  :active-value="mfaFlagConstant.MFA_VALID_FLAG"
                  :inactive-value="mfaFlagConstant.MFA_INVALID_FLAG"
                />
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 登録日時 -->
          <el-table-column
            prop="createTime"
            :label="$t('master.userMaster.createDate')"
            width="180"
            :sortable="true"
            align="left"
          >
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'
import { Form, Table } from 'element-ui'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import { i18n } from '@/locales'
import { getUserMasterData, updateUserMasterData } from '@/store/apis'
import { UserMasterData, BusinessOfficeMasterData, General, MasterData } from '@/models'
import { VALID_PATTERN, RULES, validateService } from '@/service/validateService'
import { generalService, GeneralType } from '@/commons/services/generalService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { localService, clearLocal, clearSession } from '@/commons/services/localService'
import {
  DataType,
  SymbolsBetweenCompanyAndUser,
  AdminFlgType,
  MFABindFlag,
  MFAFlag,
  UserRole
} from '@/commons/constants'
import { moveService } from '@/commons/services/moveService'
import HelperService from '@/service/helperService'
import config from '@/config/config'
import { clearWindowCookie } from '@/service/refreshAndCloseWindowService'

/**
 * ユーザマスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-user',
  components: {}
})
export default class MST_USER extends MST_BASE {
  passwordConst = '********'
  /**
   * MFA検証フラグ
   */
  mfaFlagConstant = MFAFlag

  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_USER'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'userId'

  /**
   * 画面名
   */
  pageName = i18n.t('master.userMaster.viewTitle').toString()

  /**
   * 営業所リスト
   */
  businessOfficeMasterData: BusinessOfficeMasterData[] = []

  /**
   * ユーザ種類リスト
   */
  userRoleList: General[] = []

  /**
   * 利用状況
   */
  usageStatusList: General[] = []

  /**
   * ユーザマスタ編集FORM
   */
  form = {
    tableData: []
  } as Record<string, UserMasterData[]>

  /**
   * 会社Prefix名
   *
   * @returns {string} 会社Prefix名
   */
  get companyPrefix(): string {
    return localService.userInfo?.companyPrefix + SymbolsBetweenCompanyAndUser
  }

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // 利用者ID
      userId: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.userMaster.userId')]).toString(),
          trigger: 'blur'
        },
        // 11文字以内
        {
          validator: validateService.getValidator([
            {
              rule: RULES.MAXLENGTH,
              number: 11,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.userMaster.userId'), 11]).toString()
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'userId',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.userMaster.userId')]).toString()
            }
          ]),
          trigger: 'blur'
        }
      ],
      // 利用者名
      userName: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.userMaster.userName')]).toString(),
          trigger: 'blur'
        },
        // 50文字以内
        {
          validator: validateService.getValidator([
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.userMaster.userName'), 50]).toString()
            }
          ]),
          trigger: 'blur'
        }
      ],
      password: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.userMaster.password')]).toString(),
          trigger: 'blur'
        },
        // パスワード
        {
          pattern: VALID_PATTERN[RULES.PASSWORD],
          message: i18n.t('vaildate.common.password', [i18n.t('master.userMaster.password')]).toString(),
          trigger: 'blur'
        },
        // 8~20文字
        {
          validator: validateService.getValidator([
            {
              rule: RULES.RANGE,
              range: [8, 20],
              message: i18n.t('vaildate.common.range', [i18n.t('master.userMaster.password'), 8, 20]).toString()
            },
            // 複雑さチェック
            {
              rule: RULES.MUTIPLE,
              callback: (value: string): string | undefined => {
                if (value === this.passwordConst) {
                  return
                }

                if (validateService.passwordComplexityValidate(value)) {
                  return
                }

                const message = i18n.t('vaildate.common.complexity').toString()
                return message
              }
            }
          ]),
          trigger: 'blur'
        }
      ],
      // ユーザ種類
      role: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.userMaster.role')]).toString(),
          trigger: 'blur'
        }
      ],
      // 連絡先（TEL）
      tel: [
        // TEL
        {
          pattern: VALID_PATTERN[RULES.TEL],
          message: i18n.t('vaildate.common.alphanumericAndSymbols', [i18n.t('master.userMaster.tel')]).toString(),
          trigger: 'blur'
        },
        // 16文字
        {
          validRule: [
            {
              rule: RULES.MAXLENGTH,
              number: 16,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.userMaster.tel'), 16]).toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // メール
      mail: [
        // mail
        {
          pattern: VALID_PATTERN[RULES.EMAIL],
          message: i18n.t('vaildate.common.invalidMail').toString(),
          trigger: 'blur'
        },
        // 50文字
        {
          validRule: [
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.userMaster.mail'), 50]).toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // MFAバインドフラグ
      mfaBindFlag: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.userMaster.mfaBindFlag')]).toString(),
          trigger: 'change'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('userMaster > mounted')
    this.initFormTemplate()
    this.fetchUsageStatus()
    this.fetchBusinessOfficeMaster()
    this.fetchUserRole()
    this.fetchTableData()
    this.checkPageLock()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return getUserMasterData
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {Array<any>} data 検索されたデータ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initTableData(data: Array<any>): void {
    if (!data) {
      data = []
    }

    data.forEach(item => {
      const userIdAndCompany = item.userId.split(SymbolsBetweenCompanyAndUser)
      item.password = this.passwordConst
      item.userId = userIdAndCompany.lenght === 0 ? userIdAndCompany[0] : userIdAndCompany[1]
    })

    this.oldData = cloneDeep(data)
    this.tableData = data
    this.deleteTableData = []
    this.initVisableData()
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  /**
   * ユーザ種類リストを取得する
   *
   * @returns {void}
   */
  async fetchUserRole(): Promise<void> {
    try {
      this.userRoleList = await generalService[GeneralType.USER_ROLE]
    } catch (e) {
      this.$log.debug('userMaster > fetchUserRole: e=', e)
      throw e
    }
  }

  /**
   * ユーザー権限を取得する
   *
   * @param {string} adminFlg ユーザー権限
   * @returns {boolean} 変更できるかどうか
   */
  canUserTypeChange(adminFlg: string): boolean {
    return adminFlg === AdminFlgType.SYSTEM
  }

  /**
   * 所属営業所ー権限を取得する
   *
   * @param {string} role ユーザー権限
   * @returns {boolean} 変更できるかどうか
   */
  canBusinessOfficeChange(role: string): boolean {
    return role === UserRole.SYSTEM || role === UserRole.API_CONNECT
  }

  /**
   * システム権限の場合、本社に固定する
   *
   * @param {UserMasterData} row ユーザー権限
   * @returns {void}
   */
  onRoleTypeChange(row: UserMasterData): void {
    if (row.role === UserRole.SYSTEM || row.role === UserRole.API_CONNECT) {
      row.businessOfficeId = this.businessOfficeMasterData[0].id
    }
  }

  /**
   *  セカンダリパスワード検証フラグをリセットします
   *
   * @param {string} mfaBindFlag MFAバインドフラグ
   * @returns {boolean} 変更できるかどうか
   */
  canMFABindStatusChange(mfaBindFlag: string): boolean {
    return mfaBindFlag === MFABindFlag.MFA_UNBIND_FLAGE
  }

  /**
   * 利用状況リストを取得する
   *
   * @returns {void}
   */
  async fetchUsageStatus(): Promise<void> {
    try {
      this.usageStatusList = await generalService[GeneralType.USAGE_STATUS]
    } catch (e) {
      this.$log.debug('MST_USER > fetchUsageStatus: e=', e)
      throw e
    }
  }

  /**
   * 削除前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  deleteTableDataBefore(): boolean {
    let canDelete = true
    for (const item of this.multipleSelection) {
      if (item.id === localService.userInfo?.id) {
        msgBoxService.info(i18n.t('messages.masterCommon.loginUserCantDelete').toString())
        canDelete = false
        break
      }

      // 管理者をチェックONした場合 メッセージ：管理者は削除できません
      if (item.adminFlg === AdminFlgType.SYSTEM) {
        msgBoxService.info(i18n.t('messages.masterCommon.adminUserCantDelete').toString())
        canDelete = false
        break
      }
    }

    return canDelete
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    return this.dataValidate()
  }

  /**
   * 保存後に実行したい操作。
   *
   * @returns {void}
   */
  async saveTableDataAfter(): Promise<void> {
    msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString())
    const params = cloneDeep(this.getUpsertRequest())

    const confirmCallback = async () => {
      this.destroyed = () => {
        return
      }
      moveService.beforePageChange = undefined
      moveService.beforeLogout = (): Promise<void> => {
        return Promise.resolve()
      }
      this.doLogout()
    }

    for (let index = 0; index < params.updateDataList.length; index++) {
      const item = params.updateDataList[index]
      if (item.id === localService.userInfo.id) {
        msgBoxService.info(i18n.t('messages.masterCommon.errorMessage').toString(), confirmCallback)
        return
      }
    }

    await this.fetchTableData()
  }

  /**
   * ログアウトを実行する。
   *
   * @returns {void}
   */
  async doLogout(): Promise<void> {
    clearLocal()
    clearSession()
    HelperService.removeAccessToken()
    clearWindowCookie()
    this.$router.push(config.menu.login)
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = cloneDeep(this.getUpsertRequest())
    params.addDataList.forEach((item: { userId: string; password: string }) => {
      item.userId = this.companyPrefix + item.userId
      item.password = window.btoa(item.password)
    })

    params.updateDataList.forEach((item: { userId: string; password: string }) => {
      item.userId = this.companyPrefix + item.userId
      if (item.password === this.passwordConst) {
        item.password = ''
      } else {
        item.password = window.btoa(item.password)
      }
    })
    await updateUserMasterData(params)
  }

  /**
   * 登録できる件数をチェックする
   *
   * @returns {boolean} true: 登録できる件数が最大件数を超えていない
   */
  checkInsert(): boolean {
    if (
      this.getUserCount() + this.getApiUserCount() >=
      parseInt(localService.userInfo?.idUpperLimit) + parseInt(localService.userInfo?.apiUserLimit)
    ) {
      msgBoxService.info(
        i18n.t('messages.masterCommon.maxCount', [this.pageName, localService.userInfo?.idUpperLimit]).toString()
      )
      return false
    }

    return true
  }

  /**
   * 追加ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onInsertButtonClick(): void {
    this.$log.debug(`MST_USER > onInsertButtonClick`)
    if (!this.checkInsert()) {
      return
    }

    const item = {
      ...this.formTemplate,
      dataType: DataType.INSERT,
      role: UserRole.SYSTEM,
      businessOfficeId: this.businessOfficeMasterData[0].id,
      mfaBindFlag: MFABindFlag.MFA_UNBIND_FLAGE,
      mfaFlag: MFAFlag.MFA_VALID_FLAG,
      index: this.tableData.length
    }
    this.tableData.push(item)
    this.visableTableData.push(item)
    this.clearValidate()
    this.insertAfter()
  }

  /**
   * コピーボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCopyButtonClick(): void {
    this.$log.debug(`MST_USER > ${this.pageId} > onCopyButtonClick`)
    if (!this.multipleSelection || this.multipleSelection.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataSelected').toString())
      return
    }

    if (!this.tableData) {
      return
    }

    if (
      this.getApiUserCount() + this.multipleSelection.filter(item => item.role === UserRole.API_CONNECT).length >
      parseInt(localService.userInfo?.apiUserLimit)
    ) {
      msgBoxService.info(i18n.t('messages.masterCommon.apiMaxCount', [localService.userInfo?.apiUserLimit]).toString())
      return
    }

    if (
      this.getUserCount() + this.multipleSelection.filter(item => item.role !== UserRole.API_CONNECT).length >
      parseInt(localService.userInfo?.idUpperLimit)
    ) {
      msgBoxService.info(
        i18n.t('messages.masterCommon.maxCount', [this.pageName, localService.userInfo?.idUpperLimit]).toString()
      )
      return
    }

    const multipleSelection = this.multipleSelection.map((item, index) => {
      return {
        ...item,
        id: '',
        dataType: DataType.INSERT,
        adminFlg: AdminFlgType.USER,
        createTime: '',
        password: '',
        index: this.tableData.length + index,
        mfaBindFlag: MFABindFlag.MFA_UNBIND_FLAGE
      }
    })

    const multipleSelectionClone = cloneDeep(multipleSelection)
    this.tableData.push(...multipleSelectionClone)
    this.visableTableData.push(...multipleSelectionClone)
    this.clearValidate()
    this.insertAfter()
  }

  /**
   * ユーザーの取得「利用者」
   *
   * @returns {number} ユーザー数
   */
  getUserCount(): number {
    const userData = this.tableData.filter(item => {
      return item.adminFlg === AdminFlgType.USER && item.role !== UserRole.API_CONNECT
    })

    return userData.length
  }

  /**
   * ユーザーの取得「API連携」
   *
   * @returns {number} ユーザー数
   */
  getApiUserCount(): number {
    const userData = this.tableData.filter(item => {
      return item.role === UserRole.API_CONNECT
    })

    return userData.length
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      userId: '',
      userName: '',
      password: '',
      role: '',
      businessOfficeId: '',
      tel: '',
      mail: '',
      mfaBindFlag: '',
      createTime: ''
    }
  }

  // eslint-disable-next-line max-lines-per-function
  initSearchTargets(): void {
    this.searchTargets = [
      { type: FormItemType.Input, field: 'userId' },
      { type: FormItemType.Input, field: 'userName' },
      { type: FormItemType.Input, field: 'password' },
      {
        type: FormItemType.Select,
        field: 'role',
        list: () => {
          return this.userRoleList
        }
      },
      {
        type: FormItemType.Select,
        field: 'businessOfficeId',
        list: () => {
          return this.businessOfficeMasterData.map(item => {
            return { code: item.id ? item.id : '', name: item.businessOfficeName ? item.businessOfficeName : '' }
          })
        }
      },
      { type: FormItemType.Input, field: 'tel' },
      { type: FormItemType.Input, field: 'mail' },
      {
        type: FormItemType.Select,
        field: 'mfaBindFlag',
        list: () => {
          return this.usageStatusList
        }
      },
      { type: FormItemType.Input, field: 'createTime' }
    ]
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-viewTitle {
  margin-right: 20px;
}
</style>
